<script setup lang="ts">
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAuthStore } from '@/stores/auth';

const router = useRouter();
const { t } = useI18n();
const authStore = useAuthStore();

const username = ref('');
const password = ref('');
const isLoading = ref(false);
const error = ref<string | null>(null);

const handleLogin = async () => {
  isLoading.value = true;
  error.value = null;
  try {
    await authStore.login(username.value, password.value);
    router.push({ name: 'Home' }); // ログイン成功したらホームへ
  } catch (err) {
    error.value = authStore.error || t('login.login_failed');
    console.error('Login error:', err);
  } finally {
    isLoading.value = false;
  }
};

const goToRegister = () => {
  // TODO: 登録ページがあればそこへ遷移
  alert(t('login.register_prompt'));
};
</script>

<template>
  <div class="login-container">
    <h1>{{ t('login.title') }}</h1>
    <form @submit.prevent="handleLogin" class="login-form">
      <div class="form-group">
        <label for="username">{{ t('login.username_label') }}</label>
        <input type="text" id="username" v-model="username" :placeholder="t('login.username_placeholder')" required />
      </div>
      <div class="form-group">
        <label for="password">{{ t('login.password_label') }}</label>
        <input type="password" id="password" v-model="password" :placeholder="t('login.password_placeholder')"
          required />
      </div>
      <button type="submit" :disabled="isLoading" class="submit-button">
        <span v-if="isLoading">{{ t('common.logging_in') }}</span>
        <span v-else>{{ t('login.login_button') }}</span>
      </button>
      <div v-if="error" class="error-message">{{ error }}</div>
      <p class="register-prompt">
        {{ t('login.no_account') }} <a href="#" @click.prevent="goToRegister">{{ t('login.register_now') }}</a>
      </p>
    </form>
  </div>
</template>

<style scoped>
.login-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 2rem;
  background-color: #f0f2f5;
  color: #333;
  font-family: 'Arial', sans-serif;
}

h1 {
  font-size: 2.5rem;
  color: #2c3e50;
  margin-bottom: 2rem;
}

.login-form {
  background-color: #fff;
  padding: 2.5rem;
  border-radius: 10px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  width: 100%;
  max-width: 400px;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group label {
  font-size: 1.1rem;
  color: #555;
  margin-bottom: 0.5rem;
}

.form-group input[type="text"],
.form-group input[type="password"] {
  padding: 0.8rem;
  font-size: 1rem;
  border: 1px solid #ccc;
  border-radius: 5px;
  transition: border-color 0.3s ease;
}

.form-group input[type="text"]:focus,
.form-group input[type="password"]:focus {
  border-color: #007bff;
  outline: none;
}

.submit-button {
  padding: 1rem 1.5rem;
  font-size: 1.2rem;
  color: #fff;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  background-color: #007bff;
  transition: background-color 0.3s ease;
}

.submit-button:hover:not(:disabled) {
  background-color: #0056b3;
}

.submit-button:disabled {
  background-color: #cccccc;
  cursor: not-allowed;
}

.error-message {
  color: #e74c3c;
  font-size: 1rem;
  text-align: center;
}

.register-prompt {
  text-align: center;
  font-size: 0.95rem;
  color: #666;
}

.register-prompt a {
  color: #007bff;
  text-decoration: none;
}

.register-prompt a:hover {
  text-decoration: underline;
}
</style>
